/**
 * The CAD/EDA tool a user declares their project was authored in. This is a
 * frontend-only concept: `altium`/`orcad`/`kicad` overlap with the shared
 * `CadTool` enum by name; `xpedition` is Siemens Xpedition (DxDesigner); `fusion360` is the Eagle format under its current
 * product name (it maps to the `eagle` CadTool for detection); and `other` is a
 * UI-only sentinel (never a `CadTool`). The union is kept local rather than
 * reusing the shared enum.
 */

import CableIcon from '@mui/icons-material/Cable';
import CategoryIcon from '@mui/icons-material/Category';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import MemoryIcon from '@mui/icons-material/Memory';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';

import type { CadTool, DeclarableCadTool } from '@cadstrom/sigma-engine-shared';
import type SvgIcon from '@mui/material/SvgIcon';
import type { ReactNode } from 'react';

export type ProjectFormat = 'altium' | 'orcad' | 'kicad' | 'fusion360' | 'xpedition' | 'other';

/** Every declarable format, in the order the picker offers them. */
export const PROJECT_FORMATS = [
	'altium',
	'orcad',
	'kicad',
	'fusion360',
	'xpedition',
	'other',
] as const satisfies readonly ProjectFormat[];

export const PROJECT_FORMAT_LABELS: Record<ProjectFormat, string> = {
	altium: 'Altium Designer',
	orcad: 'OrCAD',
	kicad: 'KiCad',
	fusion360: 'Fusion 360',
	xpedition: 'Siemens Xpedition',
	other: 'Other',
};

export const PROJECT_FORMAT_ICONS: Record<ProjectFormat, typeof SvgIcon> = {
	altium: DeveloperBoardIcon,
	orcad: MemoryIcon,
	kicad: CableIcon,
	fusion360: CategoryIcon,
	xpedition: SettingsInputComponentIcon,
	other: MoreHorizIcon,
};

export const DEFERRED_INSPECTION_NOTE =
	'Cadstrom will inspect your files and get back to you. Note that validation may take longer.';

/**
 * Extensions shown under each card title: the required and recommended files from
 * that format's manifest, in manifest order. OrCAD omits the `.xml` alternative to
 * `.dsn` because the manifest steers users to the original `.dsn`.
 */
export const PROJECT_FORMAT_CARD_SUBTITLES: Record<ProjectFormat, ReactNode> = {
	altium: (
		<>
			<span className='font-mono'>.PrjPcb</span>, <span className='font-mono'>.SchDoc</span>,{' '}
			<span className='font-mono'>.PcbDoc</span>
		</>
	),
	orcad: (
		<>
			<span className='font-mono'>.dsn</span>, <span className='font-mono'>.opj</span>
		</>
	),
	kicad: (
		<>
			<span className='font-mono'>.kicad_pro</span>, <span className='font-mono'>.kicad_sch</span>,{' '}
			<span className='font-mono'>.kicad_pcb</span>
		</>
	),
	fusion360: (
		<>
			<span className='font-mono'>.sch</span>, <span className='font-mono'>.brd</span>
		</>
	),
	xpedition: (
		<>
			<span className='font-mono'>.prj</span>, <span className='font-mono'>.tel</span>,{' '}
			<span className='font-mono'>.qcv</span>
		</>
	),
	other: undefined,
};

/**
 * Maps the declared `ProjectFormat` to the shared `CadTool` handed to validation
 * and the backend as the user's declared tool. `fusion360` is the Eagle format
 * under its current product name; `other` declares no tool, so it returns null.
 * The exhaustive switch makes the compiler flag any new `ProjectFormat` that
 * needs a mapping decision here.
 */
export function projectFormatToCadTool(format: ProjectFormat): DeclarableCadTool | null {
	switch (format) {
		case 'altium': {
			return 'altium';
		}
		case 'orcad': {
			return 'orcad';
		}
		case 'kicad': {
			return 'kicad';
		}
		case 'fusion360': {
			return 'eagle';
		}
		case 'xpedition': {
			return 'xpedition';
		}
		case 'other': {
			return null;
		}
	}
}

/**
 * Maps the worker's detected `CadTool` to the declared `ProjectFormat` a user could
 * have picked. `eagle` surfaces under its current product name Fusion 360; `allegro`
 * and `cadstrom` have no picker option (they land on "Other"), so they return null —
 * no switch is offered for them. The exhaustive switch makes the compiler flag any new
 * `CadTool` that needs a mapping decision here.
 */
export function cadToolToProjectFormat(tool: CadTool): ProjectFormat | null {
	switch (tool) {
		case 'altium': {
			return 'altium';
		}
		case 'orcad': {
			return 'orcad';
		}
		case 'kicad': {
			return 'kicad';
		}
		case 'eagle': {
			return 'fusion360';
		}
		case 'xpedition': {
			return 'xpedition';
		}
		case 'allegro': {
			return null;
		}
		case 'cadstrom': {
			return null;
		}
	}
}
